import { traceCrash } from '../debug/crashTrace';
import { shouldReloadForStaleBuild } from './staleBuildRecoveryPolicy';

const RELOAD_STATE_KEY = 'epos:stale-build-reload';
const RELOAD_WINDOW_MS = 30_000;

interface ReloadState {
  at: number;
  url: string;
  source: string;
}

const STALE_BUILD_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /failed to load module script/i,
  /loading chunk [\w-]+ failed/i,
  /chunkloaderror/i,
  /unable to preload css/i,
  /modulepreload/i,
];

const toSafeMessage = (value: unknown): string => {
  if (value instanceof Error) {
    return `${value.name}: ${value.message}`;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (value && typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (error) {
      console.warn('[EPOS] No se pudo serializar el error de carga dinámica.', error);
      return String(value);
    }
  }

  return String(value);
};

const getCurrentUrl = (): string =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

const readReloadState = (): ReloadState | null => {
  try {
    const rawValue = window.sessionStorage.getItem(RELOAD_STATE_KEY);
    if (!rawValue) {
      return null;
    }

    return JSON.parse(rawValue) as ReloadState;
  } catch (error) {
    console.warn('[EPOS] No se pudo leer el estado de recarga por despliegue.', error);
    return null;
  }
};

const writeReloadState = (state: ReloadState): void => {
  try {
    window.sessionStorage.setItem(RELOAD_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('[EPOS] No se pudo guardar el estado de recarga por despliegue.', error);
  }
};

const shouldReloadOnce = (source: string): boolean => {
  const currentUrl = getCurrentUrl();
  const previousState = readReloadState();
  const now = Date.now();

  if (
    previousState &&
    previousState.url === currentUrl &&
    previousState.at + RELOAD_WINDOW_MS > now
  ) {
    traceCrash('frontend.stale_build.reload_skipped', {
      source,
      previousSource: previousState.source,
      route: currentUrl,
      elapsedMs: now - previousState.at,
    });

    return false;
  }

  writeReloadState({ at: now, url: currentUrl, source });
  return true;
};

export const getDynamicImportErrorMessage = (error: unknown): string => toSafeMessage(error);

export const isStaleBuildAssetError = (error: unknown): boolean => {
  const message = toSafeMessage(error);
  return STALE_BUILD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

export const recoverFromStaleBuildAssetError = (error: unknown, source: string): boolean => {
  if (!isStaleBuildAssetError(error)) {
    return false;
  }

  if (!shouldReloadForStaleBuild(window.navigator.onLine)) {
    traceCrash('frontend.stale_build.reload_skipped_offline', {
      source,
      message: toSafeMessage(error),
      route: getCurrentUrl(),
    });
    return false;
  }

  if (!shouldReloadOnce(source)) {
    return false;
  }

  traceCrash('frontend.stale_build.reload', {
    source,
    message: toSafeMessage(error),
    route: getCurrentUrl(),
  });

  window.location.reload();
  return true;
};

export const installStaleBuildRecovery = (): void => {
  window.addEventListener('error', (event) => {
    recoverFromStaleBuildAssetError(event.error ?? event.message, 'window.error');
  });

  window.addEventListener('unhandledrejection', (event) => {
    recoverFromStaleBuildAssetError(event.reason, 'window.unhandledrejection');
  });
};
